import React, { useEffect, useState } from "react";
import {
  FlashPCBTable,
  SelectableTableRow,
  TableCell,
  TableHeaderCell,
  TableHeaderRow,
  TableRow,
} from "../../constants/Styles";
import Button from "../common/Button";
import { ButtonTypes } from "../common/Button/constants";
import RadioButton from "../common/RadioButton";
import { ConsignmentPartsHeader } from "../ReviewBoardFlow/BillOfMaterials/SelectPart/SelectPartStyles";
import AddOrEditCustomPartForm from "../ReviewBoardFlow/BillOfMaterials/SelectPart/AddOrEditCustomPartForm";
import Icon from "../common/Icon";
import ModalContentWithCloseButton from "../common/ModalContentWithCloseButton";
import LoadingIcon from "../common/LoadingIcon";
import Modal from "../common/Modal";
import useCustomConsignmentParts, {
  PartRequestType,
} from "../../utils/hooks/customParts";
import { InfoSource, Part } from "../../reducers/models/parts";
import { useAppSelector } from "../../utils/hooks/store";
import { ADMIN_GROUP, ENGINEER_GROUP } from "../../utils/group";
import {
  CustomPartContainer,
  EditButton,
  TableContainer,
  WarningContainer,
} from "./CustomPartsStyles";
import { useUser } from "../../utils/hooks/commonly-stored/users";
import SelectUser from "../SelectUser";
import Pagination from "../Pagination";
import UserPill from "../common/UserPill";
import { getIdForPart } from "../../utils/parts";

function shouldShowRadioButton(selectedPart?: Part | null) {
  return selectedPart !== undefined;
}

function CustomPartRow({
  setSelectedPartId,
  selectedPart,
  part,
  canEdit,
  fetchParts,
  isAdminOrEngineer,
}: {
  setSelectedPartId?: (part_id: string | null) => void;
  selectedPart?: Part | null;
  part: Part;
  canEdit: boolean;
  fetchParts: () => void;
  isAdminOrEngineer: boolean;
}) {
  const { user } = useUser(isAdminOrEngineer ? part.user_id : null);
  const [editPart, setEditPart] = useState(false);
  const showRadio = shouldShowRadioButton(selectedPart);

  const finishEdit = () => {
    setEditPart(false);
  };

  const RowComponent = showRadio ? SelectableTableRow : TableRow;

  return (
    <>
      <RowComponent
        onClick={() => setSelectedPartId?.(getIdForPart(part))}
        selected={part.id == selectedPart?.id}
      >
        {showRadio && (
          <TableCell>
            <RadioButton checked={part.id == selectedPart?.id} />
          </TableCell>
        )}
        <TableCell>{part.name}</TableCell>
        <TableCell>{part.short_description}</TableCell>
        {isAdminOrEngineer && (
          <TableCell>{user !== null && <UserPill user={user} />}</TableCell>
        )}
        <TableCell>
          {part.requires_stencil && (
            <Icon src="/icons/check_gray.svg" alt="checked" />
          )}
        </TableCell>
        <TableCell>{part.num_through_hole_joints}</TableCell>
        <TableCell>
          {canEdit && (
            <EditButton>
              <Icon
                src="/icons/pencil.svg"
                alt="edit"
                onClick={() => setEditPart(true)}
                styleProps={{ pointer: "cursor" }}
              />
            </EditButton>
          )}
        </TableCell>
      </RowComponent>
      <Modal isOpen={editPart} onRequestClose={finishEdit}>
        <ModalContentWithCloseButton
          title={"Edit my part"}
          closeModal={finishEdit}
        >
          <AddOrEditCustomPartForm
            userId={part.user_id as string}
            onSavePart={() => {
              fetchParts();
              setEditPart(false);
            }}
            selectedPart={part}
            isAdminOrEngineer={isAdminOrEngineer}
          />
        </ModalContentWithCloseButton>
      </Modal>
    </>
  );
}

function CustomParts({
  defaultUserId,
  setSelectedPartId,
  selectedPart,
}: {
  defaultUserId?: string | null;
  setSelectedPartId?: (part_id: string | null) => void;
  selectedPart?: Part | null;
}) {
  const { userId, groups } = useAppSelector((state) => state.auth);
  const isAdminOrEngineer =
    groups.includes(ADMIN_GROUP) || groups.includes(ENGINEER_GROUP);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const getPartsForUser = () => {
    if (isAdminOrEngineer) {
      if (selectedUserId === null) {
        return undefined;
      } else {
        return selectedUserId;
      }
    } else {
      return userId;
    }
  };
  const {
    customConsignmentPartsResponse,
    errored,
    loading,
    fetchParts,
    page,
    setPage,
  } = useCustomConsignmentParts(PartRequestType.CUSTOM, getPartsForUser());
  const [addPart, setAddPart] = useState<boolean>(false);
  const showRadio = shouldShowRadioButton(selectedPart);

  useEffect(() => {
    if (defaultUserId && selectedUserId === null) {
      setSelectedUserId(defaultUserId);
    }
  }, [defaultUserId, selectedUserId]);

  const closeModal = () => {
    setAddPart(false);
  };

  if (errored) {
    return <WarningContainer>Failed to load parts</WarningContainer>;
  }

  if (customConsignmentPartsResponse === undefined) {
    return (
      <CustomPartContainer>
        <LoadingIcon text={"Loading parts"} />
      </CustomPartContainer>
    );
  }

  const selectedPartIsInUsersList = customConsignmentPartsResponse.parts.find(
    (part) => part.part_summary.id == selectedPart?.id,
  );
  // TODO: this makes no sense with pagination sometimes
  const partsToDisplay = selectedPartIsInUsersList
    ? customConsignmentPartsResponse.parts.map((p) => p.part_summary)
    : [
        ...(selectedPart && selectedPart.source == InfoSource.MANUAL_ENTRY
          ? [selectedPart]
          : []),
        ...customConsignmentPartsResponse.parts.map((p) => p.part_summary),
      ];

  return (
    <>
      <CustomPartContainer>
        {isAdminOrEngineer && (
          <SelectUser
            selectedUserId={selectedUserId}
            setSelectedUser={(user) => setSelectedUserId(user?.id || null)}
            isClearable
          />
        )}
        {partsToDisplay.length > 0 ? (
          <>
            <TableContainer>
              <FlashPCBTable>
                <thead>
                  <TableHeaderRow>
                    {showRadio && <TableHeaderCell width={"3rem"} />}
                    <TableHeaderCell width={"12rem"}>Name</TableHeaderCell>
                    <TableHeaderCell width={"16rem"}>Notes</TableHeaderCell>
                    {isAdminOrEngineer && (
                      <TableHeaderCell width={"16rem"}>
                        Part owner
                      </TableHeaderCell>
                    )}
                    <TableHeaderCell width={"5rem"}>
                      Requires stencil
                    </TableHeaderCell>
                    <TableHeaderCell width={"10rem"}>
                      Number of through hole joints
                    </TableHeaderCell>
                    <TableHeaderCell width={"5rem"}>Actions</TableHeaderCell>
                  </TableHeaderRow>
                </thead>
                <tbody>
                  {partsToDisplay.map((part) => {
                    return (
                      <CustomPartRow
                        selectedPart={selectedPart}
                        setSelectedPartId={setSelectedPartId}
                        key={part.id}
                        part={part}
                        canEdit={part.user_id == userId || isAdminOrEngineer}
                        fetchParts={fetchParts}
                        isAdminOrEngineer={isAdminOrEngineer}
                      />
                    );
                  })}
                </tbody>
              </FlashPCBTable>
            </TableContainer>
            <Pagination
              page={page}
              startingIndex={customConsignmentPartsResponse.starting_index}
              totalCount={customConsignmentPartsResponse.total_count}
              itemsPerPage={customConsignmentPartsResponse.parts_per_page}
              itemsOnPage={customConsignmentPartsResponse.parts.length}
              onBackAction={() => setPage(page - 1)}
              onForwardAction={() => setPage(page + 1)}
              disabled={loading}
            />
          </>
        ) : (
          <ConsignmentPartsHeader>
            {"Can't find the part you're looking for? Add a part here"}
          </ConsignmentPartsHeader>
        )}
        <Button
          type={ButtonTypes.BUTTON}
          onClick={() => setAddPart(true)}
          label={"New part"}
          styleProps={{ width: "fit-content" }}
        />
      </CustomPartContainer>
      <Modal isOpen={addPart} onRequestClose={closeModal}>
        <ModalContentWithCloseButton
          title={"Add a part"}
          closeModal={closeModal}
        >
          <AddOrEditCustomPartForm
            userId={
              isAdminOrEngineer
                ? ((selectedUserId || userId) as string)
                : (userId as string)
            }
            onSavePart={(part) => {
              setAddPart(false);
              fetchParts();
              setSelectedPartId?.(getIdForPart(part));
            }}
            isAdminOrEngineer={isAdminOrEngineer}
          />
        </ModalContentWithCloseButton>
      </Modal>
    </>
  );
}

export default CustomParts;
